import React, { useState, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router-dom'
import { usePartsState } from '@/hooks/context/global/usePartsState'
import { usePartFilter, type PartSortMode } from '@/hooks/context/global/usePartFilter'
import { useCartState } from '@/hooks/context/global/useCartState'
import { useRepuestosLayout } from '@/hooks/context/global/useRepuestosLayout'
import { useCatalogPrefs } from '@/hooks/context/global/useCatalogPrefs'
import { formatPrice, normalizeCurrency } from '@/lib/currency'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { Search, X, LayoutGrid, Grid3x3, List, Filter } from 'lucide-react'
import type { SparePart, PartCategory, PartBrand } from '@/types'
import { PartCard } from '@/components/PartCard'
import { PartDialog } from '@/components/PartDialog'
import { PartView } from '@/components/PartView'
import { motion } from 'framer-motion'
import { Badge } from '@/components/ui/badge'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from '@/components/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Label } from '@/components/ui/label'
import { NumericFormat } from 'react-number-format'
import { AppCatalogFooter } from '@/components/layout/AppCatalogFooter'

/** Logo de marca de repuesto: usa el campo logo de la marca o nada. */
function getPartBrandLogo(brand: PartBrand): string | null {
  return brand.logo || null
}

export default function Repuestos() {
  const [searchParams] = useSearchParams()
  const { parts, partCategories, partBrands, loading } = usePartsState()
  const {
    filteredParts,
    selectedBrands,
    selectedCategories,
    setSelectedBrands,
    setSelectedCategories,
    searchText,
    setSearchText,
    minPrice,
    maxPrice,
    setMinPrice,
    setMaxPrice,
    updateFilters,
  } = usePartFilter()
  const addItem = useCartState((s) => s.addItem)
  const { sidebarCollapsed, searchOpen, setSearchOpen } = useRepuestosLayout()
  const { currency, viewMode, setViewMode } = useCatalogPrefs()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedPart, setSelectedPart] = useState<SparePart | null>(null)
  const [itemsPerPage, setItemsPerPage] = useState(8)
  const [currentPage, setCurrentPage] = useState(1)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sortMode, setSortMode] = useState<PartSortMode>('featured')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    updateFilters()
  }, [parts, updateFilters])

  useEffect(() => {
    const q = searchParams.get('q')
    const brandId = searchParams.get('brand')
    const categoryId = searchParams.get('category')
    const min = searchParams.get('minPrice')
    const max = searchParams.get('maxPrice')
    const sidebar = searchParams.get('sidebar')
    if (q) setSearchText(q)
    if (min) setMinPrice(Number(min) || 0)
    if (max) setMaxPrice(Number(max) || 0)
    if (brandId && partBrands.length > 0) {
      const brand = partBrands.find((b) => b.id === brandId)
      if (brand) setSelectedBrands([brand])
    }
    if (categoryId && partCategories.length > 0) {
      const cat = partCategories.find((c) => c.id === categoryId)
      if (cat) setSelectedCategories([cat])
    }
    if (sidebar === '1') setSidebarOpen(true)
  }, [searchParams, partBrands, partCategories, setSearchText, setMinPrice, setMaxPrice, setSelectedBrands, setSelectedCategories])

  useEffect(() => {
    setCurrentPage(1)
  }, [filteredParts.length])

  const catalogParts = useMemo(() => {
    let list = filteredParts
    switch (sortMode) {
      case 'price-asc':
        return [...list].sort((a, b) => (a.price ?? 0) - (b.price ?? 0))
      case 'price-desc':
        return [...list].sort((a, b) => (b.price ?? 0) - (a.price ?? 0))
      case 'name':
        return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      default:
        return list
    }
  }, [filteredParts, sortMode])

  const pageCount = Math.ceil(catalogParts.length / itemsPerPage) || 1
  const currentItems = catalogParts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)
  const handlePartClick = (part: SparePart) => {
    setSelectedPart(part)
    setDialogOpen(true)
  }
  const handleAddToCart = (part: SparePart, qty: number) => addItem(part, qty)
  const handleCategoryClick = (category: PartCategory) => {
    const next = selectedCategories.some((c) => c.id === category.id)
      ? selectedCategories.filter((c) => c.id !== category.id)
      : [...selectedCategories, category]
    setSelectedCategories(next)
    updateFilters()
  }
  const handleBrandClick = (brand: PartBrand) => {
    const next = selectedBrands.some((b) => b.id === brand.id)
      ? selectedBrands.filter((b) => b.id !== brand.id)
      : [...selectedBrands, brand]
    setSelectedBrands(next)
    updateFilters()
  }
  const clearFilters = () => {
    setSelectedBrands([])
    setSelectedCategories([])
    setSearchText('')
    setMinPrice(0)
    setMaxPrice(0)
  }
  const activeFiltersCount =
    selectedBrands.length +
    selectedCategories.length +
    (searchText ? 1 : 0) +
    (minPrice > 0 ? 1 : 0) +
    (maxPrice > 0 ? 1 : 0)
  const handleChangePage = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  const handleChangeItemsPerPage = (value: string) => {
    setItemsPerPage(Number(value))
    setCurrentPage(1)
  }

  const PartListItem = ({ part, onClick }: { part: SparePart; onClick: () => void }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-gray-200/80 shadow-[0_10px_24px_rgba(15,23,42,0.08)] hover:shadow-[0_14px_28px_rgba(15,23,42,0.14)] transition-all cursor-pointer"
      onClick={onClick}
    >
      <div className="w-32 h-24 relative rounded-xl overflow-hidden shrink-0 bg-muted">
        {part.images?.[0] ? (
          <img src={part.images[0]} alt={part.name} className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Sin imagen</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-lg font-semibold text-gray-900 truncate">{part.name}</h3>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {part.brand?.name && <span>{part.brand.name}</span>}
          {part.category?.name && (
            <>
              <span>·</span>
              <span>{part.category.name}</span>
            </>
          )}
        </div>
        {part.description && (
          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{part.description.slice(0, 100)}...</p>
        )}
      </div>
      <div className="text-right flex flex-col items-end gap-2">
        <p className="text-2xl font-bold text-[#E10F18]">
          {formatPrice(part.price ?? 0, normalizeCurrency(part.currency), currency)}
        </p>
        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onClick() }}>
          Ver detalles
        </Button>
        <Button
          size="sm"
          className="bg-[#E10F18] hover:bg-[#c00d15]"
          onClick={(e) => {
            e.stopPropagation()
            handleAddToCart(part, 1)
          }}
        >
          Añadir
        </Button>
      </div>
    </motion.div>
  )

  return (
    <>
      <Helmet>
        <title>Repuestos - Catálogo de piezas | Oriental Ramirez</title>
        <meta name="description" content="Catálogo de repuestos originales para motocicletas y vehículos. Oriental Ramirez." />
      </Helmet>

      <div className="relative flex min-h-[calc(100vh-5rem)] flex-col bg-[#F2F2F6]">
        {/* FAB Filtros — móvil */}
        <Button
          variant="default"
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden fixed bottom-20 left-4 z-40 h-12 rounded-full bg-[#1F2240] text-yellow-200 shadow-xl hover:bg-[#2A285D] px-4"
        >
          <Filter className="h-4 w-4 mr-2" />
          <span className="text-sm font-semibold">Filtros</span>
          {activeFiltersCount > 0 && (
            <Badge className="ml-2 bg-[#E10F18] text-white px-1.5 min-w-[20px] h-5 flex items-center justify-center">
              {activeFiltersCount}
            </Badge>
          )}
        </Button>

        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="w-[320px] bg-white p-0">
            <div className="p-4 space-y-5">
              <SheetHeader className="px-1">
                <SheetTitle>Filtros</SheetTitle>
              </SheetHeader>
              <ScrollArea className="h-[calc(100vh-140px)] pr-2">
                <div className="space-y-5 pb-6">
                  <div className="rounded-xl border bg-gray-50 p-3 space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Buscar</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Nombre, SKU o marca..."
                        value={searchText}
                        onChange={handleSearch}
                        className="pl-9 h-9 bg-white"
                      />
                    </div>
                  </div>
                  <div className="rounded-xl border bg-gray-50 p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Marca</p>
                      {selectedBrands.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => setSelectedBrands([])}>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                      {partBrands.map((brand) => (
                        <Button
                          key={brand.id ?? 'default'}
                          variant={selectedBrands.some((b) => b.id === brand.id) ? 'default' : 'ghost'}
                          className="w-full justify-between"
                          onClick={() => handleBrandClick(brand)}
                        >
                          <span className="flex items-center gap-2">
                            {getPartBrandLogo(brand) && (
                              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white ring-1 ring-black/10 shrink-0">
                                <img src={getPartBrandLogo(brand) as string} alt={brand.name} className="h-4 w-4 object-contain" />
                              </span>
                            )}
                            {brand.name}
                          </span>
                          {selectedBrands.some((b) => b.id === brand.id) && <Badge variant="secondary">✓</Badge>}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-xl border bg-gray-50 p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Categoría</p>
                      {selectedCategories.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => setSelectedCategories([])}>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="mt-2 space-y-1">
                      {partCategories.map((category) => (
                        <Button
                          key={category.id ?? 'default'}
                          variant={selectedCategories.some((c) => c.id === category.id) ? 'default' : 'ghost'}
                          className="w-full justify-between"
                          onClick={() => handleCategoryClick(category)}
                        >
                          {category.name}
                          {selectedCategories.some((c) => c.id === category.id) && <Badge variant="secondary">✓</Badge>}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-xl border bg-gray-50 p-3 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Precio y orden</p>
                    <div>
                      <Label className="text-xs font-semibold text-gray-700 mb-1 block">Mín. (RD$)</Label>
                      <NumericFormat
                        value={minPrice}
                        onValueChange={(v) => setMinPrice(v.floatValue ?? 0)}
                        thousandSeparator=","
                        decimalSeparator="."
                        className="flex h-9 w-full rounded-md border border-input px-3 py-2 text-sm"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-700 mb-1 block">Máx. (RD$)</Label>
                      <NumericFormat
                        value={maxPrice}
                        onValueChange={(v) => setMaxPrice(v.floatValue ?? 0)}
                        thousandSeparator=","
                        decimalSeparator="."
                        className="flex h-9 w-full rounded-md border border-input px-3 py-2 text-sm"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-700 mb-1 block">Ordenar</Label>
                      <Select value={sortMode} onValueChange={(v) => setSortMode(v as PartSortMode)}>
                        <SelectTrigger className="h-9 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="featured">Destacados</SelectItem>
                          <SelectItem value="price-asc">Precio menor a mayor</SelectItem>
                          <SelectItem value="price-desc">Precio mayor a menor</SelectItem>
                          <SelectItem value="name">Nombre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {activeFiltersCount > 0 && (
                      <Button variant="secondary" size="sm" onClick={clearFilters} className="w-full">
                        Limpiar filtros ({activeFiltersCount})
                      </Button>
                    )}
                  </div>
                </div>
              </ScrollArea>
            </div>
          </SheetContent>
        </Sheet>

        {/* Desktop sidebar */}
        <aside
          className={`hidden lg:flex fixed left-0 top-0 bottom-0 z-40 bg-[#1F2240] border-r border-white/10 shadow-2xl transition-[width,transform] duration-300 ease-in-out ${
            sidebarCollapsed ? 'w-0 -translate-x-full overflow-hidden' : 'w-80 translate-x-0'
          }`}
        >
          <div className="flex min-w-80 flex-col h-full w-80">
            <div className="px-5 pt-6 pb-4 border-b border-white/10">
              <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-200/90">Repuestos</p>
              <h2 className="text-xl font-semibold text-white mt-1">Filtros</h2>
              <p className="text-xs text-white/70 mt-1">Marca, categoría y precio.</p>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-4">
                <div className="rounded-xl bg-white/10 border border-white/20 p-3 space-y-2">
                  <Label className="text-[11px] font-semibold uppercase tracking-wide text-yellow-200">Buscar</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
                    <Input
                      type="text"
                      placeholder="Nombre, SKU o marca..."
                      value={searchText}
                      onChange={handleSearch}
                      className="pl-9 h-10 bg-white/10 border-white/20 text-white placeholder:text-white/55"
                    />
                  </div>
                  {activeFiltersCount > 0 && (
                    <Button variant="secondary" size="sm" onClick={clearFilters} className="w-full bg-white text-[#1F2240] hover:bg-white/90">
                      Limpiar filtros ({activeFiltersCount})
                    </Button>
                  )}
                </div>
                <div className="rounded-xl bg-white/10 border border-white/20 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-yellow-200">Marca</p>
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {partBrands.map((brand) => (
                      <Button
                        key={brand.id ?? 'default'}
                        variant={selectedBrands.some((b) => b.id === brand.id) ? 'default' : 'ghost'}
                        className={`w-full justify-between text-white ${selectedBrands.some((b) => b.id === brand.id) ? 'bg-black hover:bg-black/90' : 'hover:bg-white/10'}`}
                        onClick={() => handleBrandClick(brand)}
                      >
                        <span className="flex items-center gap-2">
                          {getPartBrandLogo(brand) && (
                            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white ring-1 ring-black/10 shrink-0">
                              <img src={getPartBrandLogo(brand) as string} alt={brand.name} className="h-4 w-4 object-contain" />
                            </span>
                          )}
                          {brand.name}
                        </span>
                        {selectedBrands.some((b) => b.id === brand.id) && <Badge variant="secondary">✓</Badge>}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl bg-white/10 border border-white/20 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-yellow-200">Categoría</p>
                  <div className="mt-2 space-y-1">
                    {partCategories.map((category) => (
                      <Button
                        key={category.id ?? 'default'}
                        variant={selectedCategories.some((c) => c.id === category.id) ? 'default' : 'ghost'}
                        className="w-full justify-between text-white hover:bg-white/10"
                        onClick={() => handleCategoryClick(category)}
                      >
                        {category.name}
                        {selectedCategories.some((c) => c.id === category.id) && <Badge variant="secondary">✓</Badge>}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl bg-white/10 border border-white/20 p-3 space-y-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-yellow-200">Precio y orden</p>
                  <div>
                    <Label className="text-xs font-semibold text-white mb-1 block">Mín. (RD$)</Label>
                    <NumericFormat
                      value={minPrice}
                      onValueChange={(v) => setMinPrice(v.floatValue ?? 0)}
                      thousandSeparator=","
                      decimalSeparator="."
                      className="flex h-10 w-full rounded-md border border-white/30 bg-white/20 text-white px-3 py-2 text-sm placeholder:text-white/50"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-white mb-1 block">Máx. (RD$)</Label>
                    <NumericFormat
                      value={maxPrice}
                      onValueChange={(v) => setMaxPrice(v.floatValue ?? 0)}
                      thousandSeparator=","
                      decimalSeparator="."
                      className="flex h-10 w-full rounded-md border border-white/30 bg-white/20 text-white px-3 py-2 text-sm placeholder:text-white/50"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-white mb-1 block">Ordenar</Label>
                    <Select value={sortMode} onValueChange={(v) => setSortMode(v as PartSortMode)}>
                      <SelectTrigger className="h-9 bg-white/20 text-white border-white/30">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="featured">Destacados</SelectItem>
                        <SelectItem value="price-asc">Precio menor a mayor</SelectItem>
                        <SelectItem value="price-desc">Precio mayor a menor</SelectItem>
                        <SelectItem value="name">Nombre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </ScrollArea>
            <div className="p-4 border-t border-white/10">
              <p className="text-xs text-center text-white/60">© Oriental Ramirez {new Date().getFullYear()}</p>
            </div>
          </div>
        </aside>

        <div
          className={`flex flex-1 min-h-0 flex-col transition-[padding] duration-300 ${
            sidebarCollapsed ? 'lg:pl-0' : 'lg:pl-80'
          }`}
        >
          <div className="flex flex-1 flex-col p-4 lg:p-6">
            {/* Buscador — se abre/cierra con el icono del header */}
            {searchOpen && (
              <div className="relative mb-3">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  value={searchText}
                  onChange={handleSearch}
                  placeholder="Buscar repuesto, marca o categoría..."
                  aria-label="Buscar repuestos"
                  autoFocus
                  className="h-12 rounded-xl border-gray-200 bg-white pl-11 pr-10 text-base shadow-sm"
                />
                <button
                  type="button"
                  onClick={() => {
                    setSearchText('')
                    setSearchOpen(false)
                  }}
                  aria-label="Cerrar búsqueda"
                  className="absolute right-2.5 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                  <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#38367D] border-t-transparent" />
                  <p className="text-gray-500">Cargando repuestos...</p>
                </div>
              </div>
            ) : catalogParts.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-sm max-w-md w-full">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay repuestos</h3>
                  <p className="text-gray-500 mb-4">No se encontraron piezas con los filtros actuales. Prueba a cambiar o limpiar los filtros.</p>
                  <Button variant="outline" onClick={clearFilters}>Limpiar filtros</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-gray-600">
                    {catalogParts.length} repuesto{catalogParts.length !== 1 ? 's' : ''}
                  </p>
                  <div className="flex gap-1 rounded-lg bg-white p-1.5 border border-gray-200/80 shadow-sm">
                    {([
                      { mode: 'grid' as const, icon: LayoutGrid, label: 'Vista en tarjetas grandes' },
                      { mode: 'compact' as const, icon: Grid3x3, label: 'Vista en cuadrícula compacta' },
                      { mode: 'list' as const, icon: List, label: 'Vista en lista' },
                    ]).map(({ mode, icon: Icon, label }) => (
                      <Button
                        key={mode}
                        variant={viewMode === mode ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setViewMode(mode)}
                        aria-label={label}
                        aria-pressed={viewMode === mode}
                        className={viewMode === mode ? 'bg-[#1F2240] hover:bg-[#2A285D]' : ''}
                      >
                        <Icon className="h-4 w-4" />
                      </Button>
                    ))}
                  </div>
                </div>

                {viewMode === 'list' ? (
                  <div className="flex flex-col gap-4">
                    {currentItems.map((part) => (
                      <PartListItem key={part.id} part={part} onClick={() => handlePartClick(part)} />
                    ))}
                  </div>
                ) : (
                  <div
                    className={
                      viewMode === 'compact'
                        ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-3'
                        : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5'
                    }
                  >
                    {currentItems.map((part) => (
                      <PartCard
                        key={part.id}
                        part={part}
                        dense={viewMode === 'compact'}
                        displayCurrency={currency}
                        onView={handlePartClick}
                        onAddToCart={handleAddToCart}
                      />
                    ))}
                  </div>
                )}

                {catalogParts.length > 0 && (
                  <div className="hidden lg:flex bg-white rounded-xl border border-gray-200/80 p-4 mt-6">
                    <div className="w-full flex flex-col sm:flex-row justify-between items-center gap-4">
                      <Select onValueChange={handleChangeItemsPerPage} value={itemsPerPage.toString()}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Por página" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="4">4 por página</SelectItem>
                          <SelectItem value="8">8 por página</SelectItem>
                          <SelectItem value="12">12 por página</SelectItem>
                          <SelectItem value="16">16 por página</SelectItem>
                        </SelectContent>
                      </Select>
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <Button variant="ghost" size="sm" onClick={() => handleChangePage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="text-sm">
                              Anterior
                            </Button>
                          </PaginationItem>
                          {Array.from({ length: pageCount }, (_, i) => i + 1)
                            .filter((page) => {
                              const d = Math.abs(page - currentPage)
                              return d === 0 || d === 1 || page === 1 || page === pageCount
                            })
                            .map((page, idx, arr) => {
                              if (idx > 0 && arr[idx - 1] !== page - 1) {
                                return [
                                  <PaginationItem key={`e-${page}`}>
                                    <PaginationLink>...</PaginationLink>
                                  </PaginationItem>,
                                  <PaginationItem key={page}>
                                    <PaginationLink onClick={() => handleChangePage(page)} isActive={currentPage === page}>
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>,
                                ]
                              }
                              return (
                                <PaginationItem key={page}>
                                  <PaginationLink onClick={() => handleChangePage(page)} isActive={currentPage === page}>
                                    {page}
                                  </PaginationLink>
                                </PaginationItem>
                              )
                            })}
                          <PaginationItem>
                            <Button variant="ghost" size="sm" onClick={() => handleChangePage(Math.min(pageCount, currentPage + 1))} disabled={currentPage === pageCount} className="text-sm">
                              Siguiente
                            </Button>
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  </div>
                )}

                {catalogParts.length > 0 && (
                  <div className="lg:hidden bg-white border-t border-gray-200 p-3">
                    <div className="flex justify-center">
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <Button variant="ghost" size="sm" onClick={() => handleChangePage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="text-sm">
                              Anterior
                            </Button>
                          </PaginationItem>
                          <PaginationItem>
                            <span className="px-4 text-sm text-gray-600">
                              {currentPage} de {pageCount}
                            </span>
                          </PaginationItem>
                          <PaginationItem>
                            <Button variant="ghost" size="sm" onClick={() => handleChangePage(Math.min(pageCount, currentPage + 1))} disabled={currentPage === pageCount} className="text-sm">
                              Siguiente
                            </Button>
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <AppCatalogFooter activeLink="repuestos" />
        </div>

        <PartDialog open={dialogOpen} onOpenChange={setDialogOpen}>
          {selectedPart && (
            <PartView part={selectedPart} onAddToCart={(part, qty) => { handleAddToCart(part, qty); setDialogOpen(false) }} />
          )}
        </PartDialog>
      </div>
    </>
  )
}
